import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import Icon from '../AppIcon';
import Button from './Button';
import NotificationToast from './NotificationToast';
import ThemeToggle from './ThemeToggle';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import useRealtimeNotifications from '../../hooks/useRealtimeNotifications';
import { trackMonEspaceNavigation } from '../../services/monEspaceAnalyticsService';

const Header = ({ className = '', children, ...props }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotaryModal, setShowNotaryModal] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotaireMenuOpen, setIsNotaireMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notaireMenuRef = useRef(null);
  const userMenuCloseTimer = useRef(null);
  const notaireMenuCloseTimer = useRef(null);
  const location = useLocation();

  const {
    currentLanguage,
    languages,
    isTranslating,
    translateToLanguage,
    getCurrentLanguage
  } = useTranslation();

  const { user } = useAuth();
  const { unreadCount, toasts, dismissToast } = useRealtimeNotifications(user);

  const mainNavItems = [
    { name: 'Propriétés', path: '/property-search-listings', icon: 'Home' },
    { name: 'Communes', path: '/commune-explorer', icon: 'MapPin' },
    { name: 'Investisseur', path: '/investor-mode', icon: 'TrendingUp' },
    { name: 'Services Légaux', path: '/legal-services-center', icon: 'Scale' },
    { name: 'Professionnels', path: '/professional-services-booking', icon: 'Briefcase' },
  ];

  const notaireNavItems = [
    { name: 'Trouver un Notaire', path: '/notary-appointment-booking-system', icon: 'Search' },
    { name: 'Espace Notaire', path: '/notaire/login', icon: 'LogIn' },
  ];

  const userSpaceItems = [
    { name: 'Mon Espace', path: '/mon-espace', icon: 'LayoutGrid' },
    { name: 'Démarches Admin.', path: '/demarches-administratives', icon: 'Landmark' },
    { name: 'Watchlist', path: '/investor-watchlist', icon: 'Bookmark' },
    { name: 'À Propos', path: '/about-trust-center', icon: 'Info' },
    { name: 'Dashboard Pro', path: '/professional-dashboard', icon: 'LayoutDashboard' },
    { name: 'Espace Pro', path: '/professional-portal', icon: 'UserCheck' },
    { name: 'Messagerie', path: '/in-app-messaging', icon: 'MessageSquare' },
    { name: 'Hub Notifications', path: '/notification-hub', icon: 'BellRing', badge: unreadCount },
    { name: 'Notifications', path: '/notifications', icon: 'Bell' },
    { name: 'Activités', path: '/activity-history', icon: 'ClipboardList' },
    { name: 'Digest', path: '/activity-digest', icon: 'MailOpen' },
    { name: 'Mon Profil', path: '/user-profile', icon: 'UserCircle' },
    { name: 'Gestion du profil', path: '/user-profile-management', icon: 'Settings2' },
    { name: 'Mon Compte', path: '/account-dashboard', icon: 'Settings' },
    { name: "Centre d\'aide", path: '/knowledge-base', icon: 'LifeBuoy' },
  ];

  const currentLang = getCurrentLanguage();

  const isActivePath = (path) => {
    return location?.pathname === path || location?.pathname?.startsWith(path);
  };

  const isUserSpaceActive = userSpaceItems?.some(item => isActivePath(item?.path));
  const isNotaireActive = notaireNavItems?.some(item => isActivePath(item?.path));
  const totalUserBadge = unreadCount;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsNotaireMenuOpen(false);
  }, [location?.pathname]);

  useEffect(() => {
    const autoTranslate = async () => {
      if (currentLanguage !== 'FR' && !isTranslating) {
        try {
          await translateToLanguage('FR');
        } catch (error) {
          console.error('Auto-translation to French failed:', error);
        }
      }
    };
    const timer = setTimeout(autoTranslate, 1000);
    return () => clearTimeout(timer);
  }, [currentLanguage, translateToLanguage, isTranslating]);

  const handleNotaireMouseEnter = () => {
    if (notaireMenuCloseTimer?.current) {
      clearTimeout(notaireMenuCloseTimer?.current);
      notaireMenuCloseTimer.current = null;
    }
    setIsNotaireMenuOpen(true);
  };

  const handleNotaireMouseLeave = () => {
    notaireMenuCloseTimer.current = setTimeout(() => {
      setIsNotaireMenuOpen(false);
    }, 120);
  };

  const handleUserMenuMouseEnter = () => {
    if (userMenuCloseTimer?.current) {
      clearTimeout(userMenuCloseTimer?.current);
      userMenuCloseTimer.current = null;
    }
    setIsUserMenuOpen(true);
  };

  const handleUserMenuMouseLeave = () => {
    userMenuCloseTimer.current = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 120);
  };

  return (
    <>
      {/* Notification toasts */}
      {toasts?.map((toast) => (
        <NotificationToast
          key={toast?.id}
          notification={toast}
          onDismiss={() => dismissToast(toast?.id)}
        />
      ))}

      <header
        className={cn(
          "flex items-center justify-between w-full px-6 py-3 bg-background border-b border-border sticky top-0 z-50",
          className
        )}
        {...props}
      >
        {/* Logo */}
        <Link to="/homepage" className="flex items-center gap-2.5 group flex-shrink-0">
          <div className="w-8 h-8 bg-brand-earth rounded-md flex items-center justify-center transition-opacity duration-150 group-hover:opacity-90 ml-0">
            <Icon name="Building2" size={18} color="white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-foreground tracking-tight text-[13px]">
              Kapital States
            </span>
            <span className="text-[10px] text-muted-foreground font-body leading-tight tracking-wide">
              Élever l'Immobilier
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center justify-center flex-1 mx-6 min-w-0">
          <div className="flex items-center gap-0.5">
            {mainNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={cn(
                  "relative flex items-center gap-1.5 px-3 text-sm pr-[13px] py-1.5 rounded-md font-medium transition-colors duration-150",
                  isActivePath(item?.path)
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <Icon name={item?.icon} size={14} strokeWidth={1.75} />
                <span className="whitespace-nowrap text-sm font-medium px-0">{item?.name}</span>
              </Link>
            ))}

            {/* Notaire Dropdown */}
            <div
              className="relative"
              ref={notaireMenuRef}
              onMouseEnter={handleNotaireMouseEnter}
              onMouseLeave={handleNotaireMouseLeave}
            >
              <div
                className={cn(
                  "relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 cursor-pointer",
                  isNotaireActive || isNotaireMenuOpen
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <Icon name="Scale" size={14} strokeWidth={1.75} />
                <span className="whitespace-nowrap">Notaires</span>
                <Icon
                  name={isNotaireMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={12}
                  strokeWidth={2}
                />
              </div>

              {/* Notaire Dropdown Panel */}
              {isNotaireMenuOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-52 bg-popover rounded-lg shadow-ios-large border border-border overflow-hidden z-50 animate-fade-in">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-[10px] font-body font-semibold text-muted-foreground uppercase tracking-widest">
                      Notaires
                    </p>
                  </div>
                  <div className="py-1">
                    {notaireNavItems?.map((item, index) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'both' }}
                        className={cn(
                          "relative flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors duration-100 dropdown-item-reveal",
                          isActivePath(item?.path)
                            ? 'text-foreground bg-accent font-medium'
                            : 'text-foreground hover:bg-accent'
                        )}
                      >
                        <Icon name={item?.icon} size={14} strokeWidth={1.75} className="text-muted-foreground flex-shrink-0" />
                        <span className="flex-1">{item?.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mon Espace Dropdown */}
            <div
              className="relative"
              ref={userMenuRef}
              onMouseEnter={handleUserMenuMouseEnter}
              onMouseLeave={handleUserMenuMouseLeave}
            >
              <div
                className={cn(
                  "relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 cursor-pointer",
                  isUserSpaceActive || isUserMenuOpen
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <Icon name="LayoutGrid" size={14} strokeWidth={1.75} />
                <span className="whitespace-nowrap">Mon Espace</span>
                <Icon
                  name={isUserMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={12}
                  strokeWidth={2}
                />
                {totalUserBadge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-destructive text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                    {totalUserBadge > 99 ? '99+' : totalUserBadge}
                  </span>
                )}
              </div>

              {/* Dropdown Panel */}
              {isUserMenuOpen && (
                <div className="absolute top-full right-0 mt-1.5 w-56 bg-popover rounded-lg shadow-ios-large border border-border overflow-hidden z-50 animate-fade-in">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-[10px] font-body font-semibold text-muted-foreground uppercase tracking-widest">
                      Mon Espace
                    </p>
                  </div>
                  <div className="py-1">
                    {userSpaceItems?.map((item, index) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        onClick={() => trackMonEspaceNavigation(item?.name, 'header_dropdown')}
                        style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'both' }}
                        className={cn(
                          "relative flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors duration-100 dropdown-item-reveal",
                          isActivePath(item?.path)
                            ? 'text-foreground bg-accent font-medium'
                            : 'text-foreground hover:bg-accent'
                        )}
                      >
                        <Icon name={item?.icon} size={14} strokeWidth={1.75} className="text-muted-foreground flex-shrink-0" />
                        <span className="flex-1">{item?.name}</span>
                        {item?.badge > 0 && (
                          <span className="min-w-[18px] h-[18px] px-1 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                            {item?.badge > 99 ? '99+' : item?.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                  {/* Theme Toggle inside Mon Espace */}
                  <div className="px-3 py-2.5 border-t border-border flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Apparence</span>
                    <ThemeToggle />
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Desktop CTA Buttons */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0 ml-2">
          <div className="flex flex-row items-center gap-2">
            {!user && (
              <Link to="/auth">
                <Button variant="default" size="sm" iconName="LogIn" iconPosition="left">
                  Connexion
                </Button>
              </Link>
            )}
            <Link to="/professional-signup" className="flex-shrink-0">
              <Button variant="outline" size="sm" iconName="Briefcase" iconPosition="left">
                Devenir Prestataire
              </Button>
            </Link>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-1 flex-shrink-0">
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md hover:bg-accent transition-colors duration-150"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? "X" : "Menu"}
              size={20}
              color="var(--color-foreground)"
              strokeWidth={1.75}
            />
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-[57px] left-0 right-0 bg-background border-b border-border shadow-ios-medium animate-slide-up z-40 overflow-y-auto max-h-[calc(100vh-57px)]">
          <nav className="px-4 py-4 space-y-0.5">
            {mainNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150",
                  isActivePath(item?.path)
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <Icon name={item?.icon} size={16} strokeWidth={1.75} />
                <span>{item?.name}</span>
              </Link>
            ))}

            {/* Notaires section in mobile */}
            <div className="pt-2 border-t border-border mt-2">
              <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Notaires
              </p>
              {notaireNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150",
                    isActivePath(item?.path)
                      ? 'text-foreground bg-accent'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <Icon name={item?.icon} size={16} strokeWidth={1.75} />
                  <span>{item?.name}</span>
                </Link>
              ))}
            </div>

            <div className="pt-2 border-t border-border mt-2">
              <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Mon Espace
              </p>
              {userSpaceItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150",
                    isActivePath(item?.path)
                      ? 'text-foreground bg-accent'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <Icon name={item?.icon} size={15} strokeWidth={1.75} />
                  <span className="flex-1">{item?.name}</span>
                  {item?.badge > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {item?.badge > 99 ? '99+' : item?.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            <div className="pt-3 border-t border-border flex flex-col gap-2">
              {/* Theme Toggle in mobile menu */}
              <div className="flex items-center justify-between px-3 py-2 rounded-md bg-accent/50">
                <span className="text-sm font-medium text-foreground">Apparence</span>
                <ThemeToggle />
              </div>
              {!user && (
                <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="default" size="sm" fullWidth iconName="LogIn" iconPosition="left">
                    Connexion
                  </Button>
                </Link>
              )}
              <Link to="/professional-signup" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="outline" size="sm" fullWidth iconName="Briefcase" iconPosition="left">
                  Devenir Prestataire
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;